import {
  getBuildingDisplayHeight,
  withBuildingDisplayHeight,
} from '@popety-io/utils'
export const DEFAULT_BUILDING_FLOOR_HEIGHT_M = 3
export const DEFAULT_BUILDING_HEIGHT_M = 10

const toPositiveNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

export const getBuildingFeatureHeight = (
  properties: Record<string, unknown> | null | undefined,
  fallback = DEFAULT_BUILDING_HEIGHT_M
): number => {
  const props = properties || {}

  const explicitHeight =
    getBuildingDisplayHeight(props.height_estimate_to_display) ??
    toPositiveNumber(props.height_estimate) ??
    toPositiveNumber(props.height) ??
    toPositiveNumber(props.max_height) ??
    toPositiveNumber(props.building_height)

  if (explicitHeight) return explicitHeight

  const floors =
    toPositiveNumber(props.floor_nb) ??
    toPositiveNumber(props.residential_floor_nb) ??
    toPositiveNumber(props.floors)

  if (floors) return floors * DEFAULT_BUILDING_FLOOR_HEIGHT_M

  return fallback
}

export const getBuildingGeoJsonHasHeight = (
  buildingGeoJson: any,
  buildingHeight?: number
): boolean => {
  if (toPositiveNumber(buildingHeight)) return true

  if (buildingGeoJson?.type === 'FeatureCollection') {
    return buildingGeoJson.features?.some(
      (feature: any) => getBuildingFeatureHeight(feature.properties) > 0
    )
  }

  return Boolean(buildingGeoJson)
}

export const getBuildingExtrusionHeight = (
  buildingGeoJson: any,
  buildingHeight?: number,
  buildingDisplayHeight?: number
): number => {
  const properties =
    buildingGeoJson?.type === 'Feature' ? buildingGeoJson.properties : null
  return (
    getBuildingDisplayHeight(buildingDisplayHeight) ??
    getBuildingFeatureHeight(
      properties,
      buildingHeight || DEFAULT_BUILDING_HEIGHT_M
    )
  )
}

export const getBuildingHeightExpression = (
  fallback = DEFAULT_BUILDING_HEIGHT_M
) =>
  withBuildingDisplayHeight([
    'case',
    ['>', ['to-number', ['get', 'height']], 0],
    ['to-number', ['get', 'height']],
    fallback,
  ])

// Per-feature opacity for the 3D extrusion: fade projected buildings, mostly
// hide underground ones, keep normal ones at a slight transparency.
export const BUILDING_3D_UNDERGROUND_ALPHA = 0.1
export const BUILDING_3D_PROJECTED_ALPHA = 0.3
export const BUILDING_3D_DEFAULT_ALPHA = 0.7

/**
 * Build a per-feature `fill-extrusion-color` expression that bakes the
 * underground/projected/normal opacity into the color's alpha channel.
 *
 * IMPORTANT: Mapbox GL does NOT support a data-driven `fill-extrusion-opacity`.
 * Passing an expression there silently disables the *entire* extrusion layer
 * (no error is thrown — it is swallowed by the map error listener), which makes
 * the 3D buildings disappear. Always keep `fill-extrusion-opacity` constant and
 * express per-feature transparency through the color alpha instead.
 */
export const getBuilding3DColorExpression = (baseColor: unknown) => [
  'let',
  'rgb',
  ['to-rgba', baseColor],
  [
    'rgba',
    ['at', 0, ['var', 'rgb']],
    ['at', 1, ['var', 'rgb']],
    ['at', 2, ['var', 'rgb']],
    [
      'case',
      ['==', ['get', 'is_underground'], true],
      BUILDING_3D_UNDERGROUND_ALPHA,
      ['==', ['get', 'is_projected'], true],
      BUILDING_3D_PROJECTED_ALPHA,
      BUILDING_3D_DEFAULT_ALPHA,
    ],
  ],
]
